import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  tenantId?: string;
  tenantName?: string;
  isPlatformAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, tenantId?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  tenantId: string | null;
  switchTenant?: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedTenantId = localStorage.getItem('tenantId');

    if (token && savedUser) {
      try {
        // Set tenant header if available
        if (savedTenantId) {
          api.defaults.headers.common['X-Tenant-ID'] = savedTenantId;
          setTenantId(savedTenantId);
        }
        
        // Verify token is still valid
        // Check if this is a platform admin based on saved user data
        const parsedUser = JSON.parse(savedUser);
        const isPlatformAdmin = parsedUser.isPlatformAdmin || parsedUser.role === 'platformAdmin';
        
        const endpoint = isPlatformAdmin ? '/platform/auth/me' : '/auth/me';
        const response = await api.get(endpoint);
        setUser(response.data.user);
        
        // Update tenant context from user data
        if (response.data.user.tenantId) {
          setTenantId(response.data.user.tenantId);
          localStorage.setItem('tenantId', response.data.user.tenantId);
          api.defaults.headers.common['X-Tenant-ID'] = response.data.user.tenantId;
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');
        delete api.defaults.headers.common['X-Tenant-ID'];
      }
    }
    setLoading(false);
  };

  const login = async (email: string, password: string, requestedTenantId?: string): Promise<void> => {
    try {
      // Check if this is a platform admin login
      const platformAdmins = ['admin@equityengine.com']; // Could be expanded later
      const isPlatformAdminLogin = platformAdmins.includes(email) && !requestedTenantId;
      
      let response;
      if (isPlatformAdminLogin) {
        // Use platform auth endpoint
        response = await api.post('/platform/auth/login', { email, password });
      } else {
        // Regular tenant login
        const headers: any = {};
        if (requestedTenantId) {
          headers['X-Tenant-ID'] = requestedTenantId;
        }
        response = await api.post('/auth/login', { email, password }, { headers });
      }
      
      const { token, user: userData } = response.data;

      // Check if user has appropriate role
      const allowedRoles = ['admin', 'manager', 'platformAdmin'];
      if (!userData.isPlatformAdmin && !allowedRoles.includes(userData.role)) {
        throw new Error('Access denied. Admin privileges required.');
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      // Set tenant context
      if (userData.tenantId) {
        setTenantId(userData.tenantId);
        localStorage.setItem('tenantId', userData.tenantId);
        api.defaults.headers.common['X-Tenant-ID'] = userData.tenantId;
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    delete api.defaults.headers.common['X-Tenant-ID'];
    setUser(null);
    setTenantId(null);
  };

  const switchTenant = async (newTenantId: string): Promise<void> => {
    if (!user?.isPlatformAdmin) {
      throw new Error('Only platform admins can switch tenants');
    }
    
    // If empty string, switch to platform view (no tenant)
    if (newTenantId === '') {
      setTenantId(null);
      localStorage.removeItem('tenantId');
      delete api.defaults.headers.common['X-Tenant-ID'];
      
      // Keep platform admin user data
      setUser({
        ...user,
        tenantId: undefined,
        tenantName: undefined
      });
      
      // Reload the page to refresh all components
      window.location.reload();
      return;
    }
    
    // Switch to specific tenant
    setTenantId(newTenantId);
    localStorage.setItem('tenantId', newTenantId);
    api.defaults.headers.common['X-Tenant-ID'] = newTenantId;
    
    // Update user data with tenant context
    try {
      const response = await api.get('/platform/tenants/' + newTenantId);
      const tenant = response.data;
      
      setUser({
        ...user,
        tenantId: newTenantId,
        tenantName: tenant.name
      });
      
      // Reload the page to refresh all components with new tenant context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    tenantId,
    switchTenant: user?.isPlatformAdmin ? switchTenant : undefined,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};